import { appAlert, appConfirm } from "./appDialogs";
import { API_ORIGIN, apiUrl } from "./apiOrigin";
// usage_grid.js — "Usage by product", the one implementation.
//
// WHY ITS OWN FILE
//
// This grid existed twice: inline in stock_excess.js and again inside
// usage_tools.js. The two drifted immediately - the overview's copy was reading
// a month-keyed row shape the API stopped returning, so every figure on it read
// zero while the detail screen was fine. Both screens now import this, so a
// change to the payload breaks or fixes them together.
//
// Props:
//   open               render the dialog
//   onClose            called by Close and by the backdrop
//   groupId            the organisation (localStorage "userId" on both screens)
//   members            [{ pharmacyId, pharmacyName }]
//   defaultPharmacyId  pre-selects a branch
//   masked             hide branch names for screen sharing
//   inventory          the group's stock documents, for pack prices. Optional:
//                      without it the price and cost columns read "—" and the
//                      money filters are disabled rather than silently wrong.

import React, { useState, useMemo, useEffect } from "react";
import PmrMatchLines, { RemapDialog } from "./PmrMatchLines";
import * as XLSX from "xlsx";
import { displayName } from "./nameMask";
import { normaliseDrug, usageConfidence } from "./estate_model";
const API = apiUrl("");
const token = () => localStorage.getItem("authToken");

// Keep the display bounded; Excel exports fetch the complete matching dataset.
const DISPLAY_LIMIT = 2000;
const money = n => n == null ? "\u2014" : `£${n.toLocaleString("en-GB", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2
})}`;
const num = v => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : null;
};

/* Pack prices, keyed the way the rest of the module keys drugs.
   Median rather than mean or latest: one mistyped price on one branch's stock
   line should not move the estate's figure for that product, and it regularly
   does - a £4 box booked in as 400 is the commonest data error here. */
function buildPriceIndex(inventory) {
  const bySnomed = {};
  const byName = {};
  const push = (bag, key, v) => {
    if (!key) return;
    if (!bag[key]) bag[key] = [];
    bag[key].push(v);
  };
  (Array.isArray(inventory) ? inventory : []).forEach(it => {
    // price is held in pence on the stock document, as everywhere else here
    const pence = parseFloat(it.price || 0);
    if (!(pence > 0)) return;
    const gbp = pence / 100;
    push(bySnomed, it.snomed && String(it.snomed), gbp);
    push(byName, normaliseDrug(it.vmppNm || it.drugName || it.drug).key, gbp);
  });
  const median = arr => {
    const s = [...arr].sort((a, b) => a - b);
    return s.length ? s[Math.floor((s.length - 1) / 2)] : null;
  };
  const flatten = bag => {
    const out = {};
    Object.keys(bag).forEach(k => {
      out[k] = median(bag[k]);
    });
    return out;
  };
  return {
    bySnomed: flatten(bySnomed),
    byName: flatten(byName)
  };
}

/* The same verdict the estate overview puts on a line, from the same function,
   so the two screens cannot reach different conclusions about one product.

   One local addition. usageConfidence deliberately treats a zero month as
   data - dispensing none of something in May is an observation, and the excess
   engine needs that, because slow movers are where excess collects. For
   deciding whether an AVERAGE is worth ordering against, the opposite holds: a
   month at zero, or a month missing from the axis entirely, means the average
   is over a gap. So those are forced to "none" here and nowhere else. */
const CONF = {
  high: {
    label: "steady",
    colour: "#639922",
    rank: 3
  },
  medium: {
    label: "fair",
    colour: "#BA7517",
    rank: 2
  },
  low: {
    label: "unsure",
    colour: "#888780",
    rank: 1
  },
  none: {
    label: "no usage",
    colour: "#CF1322",
    rank: 0
  }
};
const CONF_CHOICES = [{
  value: "high",
  label: "Steady only"
}, {
  value: "medium",
  label: "Fair or better"
}, {
  value: "low",
  label: "Any, but no gaps"
}];
const DEFAULT_MIN_CONF = "medium";
function rowConfidence(row, axisLength) {
  const ms = Array.isArray(row.months) ? row.months : [];
  if (axisLength && ms.length !== axisLength) {
    return {
      level: "none",
      cv: null,
      months: ms.length,
      why: "a month is missing from the record"
    };
  }
  if (ms.some(m => m == null || m.units == null || !(m.units > 0))) {
    return {
      level: "none",
      cv: null,
      months: ms.length,
      why: "a month has no dispensing at all"
    };
  }
  return usageConfidence(ms.map(m => ({
    qty: m.units
  })));
}
const th = {
  textAlign: "left",
  padding: "7px 9px",
  background: "#EDF2F7",
  borderBottom: "1px solid #D9D9D9",
  whiteSpace: "nowrap",
  fontSize: "0.9em"
};
const td = {
  padding: "7px 9px",
  borderBottom: "1px solid #e8edf3",
  verticalAlign: "middle"
};
const fieldLabel = {
  fontSize: "0.72em",
  textTransform: "uppercase",
  letterSpacing: ".4px",
  color: "#5D5D5D",
  fontWeight: 700,
  marginBottom: 3
};
function Field({
  label,
  hint,
  children
}) {
  return <div title={hint} style={{
    display: "flex",
    flexDirection: "column"
  }}>
      <div style={fieldLabel}>{label}</div>
      {children}
    </div>;
}
export function LegacyUsageGridModal({
  open,
  onClose,
  groupId,
  members = [],
  defaultPharmacyId = "",
  masked = false,
  inventory = null
}) {
  const [pharmacyId, setPharmacyId] = useState(defaultPharmacyId || "");
  const [drug, setDrug] = useState("");
  const [aggregate, setAggregate] = useState(false);
  const [rows, setRows] = useState([]);
  const [months, setMonths] = useState([]);
  const [busy, setBusy] = useState(false);
  const [exportBusy, setExportBusy] = useState(false);
  const [searched, setSearched] = useState(false);

  // Value filters. Kept as strings so a half-typed "1." doesn't reset the box.
  const [minPrice, setMinPrice] = useState("");
  const [minSpend, setMinSpend] = useState("");
  const [minPackets, setMinPackets] = useState("");
  // Consistency, not volume: how trustworthy the three months are before the
  // average is worth ordering against. Off by default - it is a judgement about
  // data quality, and switching it on silently would hide real lines.
  const [minConf, setMinConf] = useState(DEFAULT_MIN_CONF);
  const [applyConf, setApplyConf] = useState(false);
  const [cellDetail, setCellDetail] = useState(null); // {row, month, label, rows}
  const [cellBusy, setCellBusy] = useState(false);
  const [branchDetail, setBranchDetail] = useState(null); // {row, branches}
  // which product rows have their PMR match breakdown open. The name in this
  // column is the OUTPUT of a match, and several PMR spellings collapse into
  // it, so it opens to show what is actually counted underneath.
  const [openMatches, setOpenMatches] = useState({});
  // Lines the branch dispensed that never became a dm+d product. Shown so the
  // manager can see them, kept out of every total so they cannot quietly
  // inflate an average nobody can trace to a product.
  const [unmatchedRows, setUnmatchedRows] = useState([]);
  const [showUnmatched, setShowUnmatched] = useState(true);
  /* Three lists rather than one. Medications is the default because it is
     what a manager is usually after; supplements are a handful of products
     carrying most of the volume; appliances are most of the lines and almost
     none of the units. */
  const [category, setCategory] = useState("medication");
  const [moving, setMoving] = useState(null);
  const [allocating, setAllocating] = useState(null);
  const [allocBusy, setAllocBusy] = useState(null);
  const [branchBusy, setBranchBusy] = useState(false);

  // The dialog stays mounted so its results survive a close and re-open, which
  // means the branch has to be re-seeded on each open rather than at mount -
  // otherwise it keeps whichever branch was selected the first time the screen
  // rendered, which is usually none.
  useEffect(() => {
    if (open) setPharmacyId(defaultPharmacyId || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  const priceIndex = useMemo(() => buildPriceIndex(inventory), [inventory]);
  const havePrices = Object.keys(priceIndex.bySnomed).length > 0 || Object.keys(priceIndex.byName).length > 0;

  // A price the backend sends wins; the stock documents are the fallback.
  const priceOf = r => {
    const fromApi = num(r.packPrice != null ? r.packPrice : r.price);
    if (fromApi != null && fromApi > 0) return fromApi;
    const s = r.snomed && priceIndex.bySnomed[String(r.snomed)];
    if (s != null) return s;
    const n = priceIndex.byName[normaliseDrug(r.drugname).key];
    return n != null ? n : null;
  };

  // Monthly spend, not monthly value of what is held. perMonthPackets is the
  // figure every other screen orders and transfers against, so the money has to
  // hang off the same number or the two will disagree in front of a client.
  const spendOf = r => {
    const p = priceOf(r);
    if (p == null || r.perMonthPackets == null) return null;
    return p * r.perMonthPackets;
  };
  const CATEGORY_LABEL = {
    medication: "Medications",
    supplement: "Supplements",
    appliance: "Dressings and appliances"
  };

  /** Move one line into another of the three groups. It sticks: the next
   *  search puts it where you put it, for this organisation only. */
  const moveCategory = async (row, to) => {
    setMoving(row.snomed || row.matchKey);
    try {
      const res = await fetch(`${API}/usageCategoryOverride`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token()}`
        },
        body: JSON.stringify({
          groupId,
          category: to,
          ...(row.snomed ? {
            snomed: row.snomed
          } : {
            key: row.matchKey
          })
        })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        (await appAlert(data.error || "Could not move that line"));
        return;
      }
      await run();
    } catch (e) {
      (await appAlert(`Move failed: ${e.message}`));
    } finally {
      setMoving(null);
    }
  };

  /** Place an unmatched line on a dm+d product, then reload so it moves up
   *  into the counted rows where it belongs. */
  const allocate = async (row, vppid, name) => {
    if (!(await appConfirm(`Count "${row.drugname}" as:\n\n${name}\n\n` + `${row.total.toLocaleString()} units over this window will be written to usage, ` + `and future uploads of this line will go there automatically.`))) return;
    setAllocBusy(row.matchKey);
    try {
      const res = await fetch(`${API}/pmrUnmatchedAccept`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token()}`
        },
        body: JSON.stringify({
          groupId,
          key: row.matchKey,
          vppid
        })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        (await appAlert(data.error || "Could not allocate that line"));
        return;
      }
      setAllocating(null);
      await run();
    } catch (e) {
      (await appAlert(`Allocation failed: ${e.message}`));
    } finally {
      setAllocBusy(null);
    }
  };
  const priced = useMemo(() => rows.map(r => ({
    ...r,
    _price: priceOf(r),
    _spend: spendOf(r),
    _conf: rowConfidence(r, months.length)
  })),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [rows, months, priceIndex]);
  const fMinPrice = num(minPrice);
  const fMinSpend = num(minSpend);
  const fMinPackets = num(minPackets);
  const minRank = (CONF[minConf] || CONF[DEFAULT_MIN_CONF]).rank;
  const filtersOn = fMinPrice != null || fMinSpend != null || fMinPackets != null || applyConf;

  // Used by both the display and the complete export dataset.
  const passesFilters = r => {
    if (fMinPackets != null && !((r.perMonthPackets ?? 0) >= fMinPackets)) return false;
    // A line with no price cannot clear a money filter. Hidden rather than let
    // through: "at least £20 a month" that quietly includes unknowns is worse
    // than a count of what it dropped, which is in the banner below.
    if (fMinPrice != null && !(r._price != null && r._price >= fMinPrice)) return false;
    if (fMinSpend != null && !(r._spend != null && r._spend >= fMinSpend)) return false;
    if (applyConf && CONF[r._conf.level].rank < minRank) return false;
    return true;
  };
  const visible = useMemo(() => priced.filter(passesFilters),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [priced, fMinPrice, fMinSpend, fMinPackets, applyConf, minRank]);
  const totalSpend = useMemo(() => visible.reduce((a, r) => a + (r._spend || 0), 0), [visible]);
  const unsteadyHidden = useMemo(() => applyConf ? priced.filter(r => CONF[r._conf.level].rank < minRank).length : 0, [priced, applyConf, minRank]);
  const unpricedHidden = useMemo(() => fMinPrice != null || fMinSpend != null ? priced.filter(r => r._price == null).length : 0, [priced, fMinPrice, fMinSpend]);
  const run = async () => {
    try {
      setBusy(true);
      const res = await fetch(`${API}/usageGrid`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token()}`
        },
        body: JSON.stringify({
          groupId,
          pharmacyId: pharmacyId || undefined,
          drug: drug || undefined,
          aggregate,
          category,
          limit: DISPLAY_LIMIT
        })
      });
      // Surfaced rather than swallowed: a 401, a CORS block and a genuinely
      // empty result all look like an empty table otherwise.
      if (!res.ok) {
        const body = await res.text().catch(() => "");
        (await appAlert(`Search failed: HTTP ${res.status}${body ? `\n\n${body.slice(0, 300)}` : ""}`));
        setRows([]);
        return;
      }
      const data = await res.json();
      if (!data || !Array.isArray(data.rows)) {
        (await appAlert(`Search returned an unexpected response:\n\n${JSON.stringify(data).slice(0, 300)}`));
        setRows([]);
        return;
      }
      setMonths(data.months || []);
      setRows(data.rows);
      setUnmatchedRows(Array.isArray(data.unmatchedRows) ? data.unmatchedRows : []);
      setSearched(true);
      setCellDetail(null);
      setBranchDetail(null);
      if (data.rows.length === 0 && !(data.unmatchedRows || []).length) {
        (await appAlert("No usage matched that search."));
      }
    } catch (e) {
      (await appAlert(`Search error: ${e.message}`));
    } finally {
      setBusy(false);
    }
  };
  const openCell = async (row, m) => {
    try {
      setCellBusy(true);
      setCellDetail({
        row,
        month: m.month,
        label: m.label,
        rows: null
      });
      const res = await fetch(`${API}/usageCell`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token()}`
        },
        body: JSON.stringify({
          groupId,
          snomed: row.snomed,
          drugname: row.drugname,
          month: m.month,
          // an aggregated row has no single branch, so ask for all of them
          pharmacyId: row.aggregated ? undefined : row.pharmacyId
        })
      });
      if (!res.ok) {
        const body = await res.text().catch(() => "");
        (await appAlert(`Could not load the detail: HTTP ${res.status}${body ? `\n\n${body.slice(0, 200)}` : ""}`));
        setCellDetail(null);
        return;
      }
      const data = await res.json();
      setCellDetail({
        row,
        month: m.month,
        label: m.label,
        rows: data.rows || [],
        totalUnits: data.totalUnits
      });
    } catch (e) {
      (await appAlert(`Error loading detail: ${e.message}`));
      setCellDetail(null);
    } finally {
      setCellBusy(false);
    }
  };
  const openBranchBreakdown = async row => {
    if (branchDetail && branchDetail.row.snomed === row.snomed && branchDetail.row.drugname === row.drugname) {
      setBranchDetail(null); // second click closes it
      return;
    }
    try {
      setBranchBusy(true);
      setBranchDetail({
        row,
        branches: null
      });
      const res = await fetch(`${API}/usageBranchBreakdown`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token()}`
        },
        body: JSON.stringify({
          groupId,
          snomed: row.snomed,
          drugname: row.drugname
        })
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok || d.error) {
        (await appAlert(`Could not load the branch breakdown: ${d.error || `HTTP ${res.status}`}`));
        setBranchDetail(null);
        return;
      }
      setBranchDetail({
        row,
        branches: d.branches || []
      });
    } catch (e) {
      (await appAlert(`Could not load the branch breakdown: ${e.message}`));
      setBranchDetail(null);
    } finally {
      setBranchBusy(false);
    }
  };

  // Fetch every matching product, then apply the same local value filters.
  const exportExcel = async () => {
    if (exportBusy) return;
    setExportBusy(true);
    try {
      const res = await fetch(`${API}/usageGridExport`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token()}`
        },
        body: JSON.stringify({
          groupId,
          pharmacyId: pharmacyId || undefined,
          drug: drug || undefined,
          aggregate,
          category
        })
      });
      if (!res.ok) {
        const body = await res.text().catch(() => "");
        throw new Error(`HTTP ${res.status}${body ? `\n\n${body.slice(0, 300)}` : ""}`);
      }
      const data = await res.json();
      if (!data || !Array.isArray(data.rows) || !Array.isArray(data.months)) {
        throw new Error("The server returned an unexpected export response.");
      }
      const exportMonths = data.months;
      const exportRows = data.rows.map(r => ({
        ...r,
        _price: priceOf(r),
        _spend: spendOf(r),
        _conf: rowConfidence(r, exportMonths.length)
      })).filter(passesFilters);
      if (exportRows.length === 0) {
        (await appAlert("No usage matched the selected export filters."));
        return;
      }
      const header = ["Pharmacy", "Product", "Pack", "Pack price (£)", ...exportMonths.map(m => `${m.label} (packets)`), "Avg packets per month", "Avg spend per month (£)", "Confidence", "Month spread %", "Basis", "Estimated split"];
      const body = exportRows.map(r => {
        const byMonth = {};
        (r.months || []).forEach(m => {
          byMonth[m.month] = m.packets != null ? m.packets : m.units || 0;
        });
        return [r.aggregated ? "All branches" : displayName(r.pharmacyName || r.pharmacyId, masked), r.drugname, r.packSize ? `${r.packSize}${r.unit || ""}` : "", r._price != null ? Number(r._price.toFixed(2)) : "", ...exportMonths.map(m => byMonth[m.month] != null ? byMonth[m.month] : 0), r.perMonthPackets != null ? r.perMonthPackets : r.perMonthUnits ?? 0, r._spend != null ? Number(r._spend.toFixed(2)) : "", CONF[r._conf.level].label, r._conf.cv != null ? Math.round(r._conf.cv * 100) : "", r.basis || "", r.apportioned ? "yes" : ""];
      });
      const ws = XLSX.utils.aoa_to_sheet([header, ...body]);
      ws["!cols"] = header.map((h, i) => ({
        wch: i === 1 ? 42 : Math.max(12, h.length + 2)
      }));
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Usage");
      const stamp = new Date().toISOString().slice(0, 10);
      XLSX.writeFile(wb, `usage_by_product_${stamp}.xlsx`);
    } catch (e) {
      (await appAlert(`Export failed: ${e.message}`));
    } finally {
      setExportBusy(false);
    }
  };
  const clearFilters = () => {
    setMinPrice("");
    setMinSpend("");
    setMinPackets("");
    setMinConf(DEFAULT_MIN_CONF);
    setApplyConf(false);
  };
  if (!open) return null;
  const inputStyle = {
    padding: "6px 8px",
    border: "1px solid #D9D9D9",
    borderRadius: 4
  };
  const maybeCapped = rows.length >= DISPLAY_LIMIT;
  return <>
      <div style={{
      position: "fixed",
      inset: 0,
      background: "rgba(15,23,42,0.5)",
      zIndex: 1600
    }} onClick={onClose} />
      <div style={{
      position: "fixed",
      top: "3vh",
      left: "50%",
      transform: "translateX(-50%)",
      width: 1680,
      maxWidth: "97vw",
      maxHeight: "94vh",
      overflowY: "auto",
      zIndex: 1610,
      background: "#fff",
      border: "1px solid #D9D9D9",
      borderRadius: 10,
      boxShadow: "0 16px 40px rgba(15,23,42,0.35)",
      padding: "16px 18px"
    }}>

        {/* ---- title ---- */}
        <div style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
        gap: 12,
        marginBottom: 12
      }}>
          <div>
            <div style={{
            fontWeight: 700,
            fontSize: "1.15em"
          }}>Usage by pharmacy and product</div>
            <div style={{
            fontSize: "0.85em",
            color: "#5D5D5D",
            marginTop: 3,
            lineHeight: 1.5,
            maxWidth: 900
          }}>
              What each branch actually gets through, month by month, and what that costs.
              This is the figure every excess and transfer number on these screens is built
              on — if it looks wrong here, it is wrong everywhere.
            </div>
          </div>
          <button onClick={onClose} style={{
          padding: "7px 16px",
          fontWeight: 600,
          border: "none",
          borderRadius: 5,
          background: "#2b7de9",
          color: "#fff"
        }}>Close</button>
        </div>

        {/* ---- control panel: what to fetch, then what is worth showing ---- */}
        <div style={{
        border: "1px solid #dfe3e8",
        borderRadius: 6,
        background: "#FAFAFA",
        padding: "10px 12px",
        marginBottom: 10
      }}>

          {/* row 1 — the search itself */}
          <div style={{
          display: "flex",
          gap: 14,
          flexWrap: "wrap",
          alignItems: "flex-end"
        }}>
            <Field label="Branch" hint="One branch, or every branch in the group.">
              <select value={pharmacyId} onChange={e => setPharmacyId(e.target.value)} disabled={aggregate} style={{
              ...inputStyle,
              minWidth: 200
            }}>
                <option value="">All pharmacies</option>
                {members.map(m => <option key={m.pharmacyId} value={m.pharmacyId}>{displayName(m.pharmacyName, masked)}</option>)}
              </select>
            </Field>

            <Field label="Product" hint="Part of a product name. Leave empty for everything.">
              <input type="text" value={drug} onChange={e => setDrug(e.target.value)} onKeyDown={e => {
              if (e.key === "Enter") run();
            }} placeholder="Search product name" style={{
              ...inputStyle,
              minWidth: 260
            }} />
            </Field>

            <Field label="Group" hint="Three lists rather than one. Medications is what you usually want; supplements are a handful of products carrying most of the volume; dressings and appliances are most of the lines and almost none of the units.">
              <div style={{
              display: "flex",
              flexDirection: "column",
              gap: 3,
              padding: "4px 0"
            }}>
                {["medication", "supplement", "appliance"].map(c => <label key={c} style={{
                display: "flex",
                gap: 6,
                alignItems: "center",
                fontSize: "0.9em",
                whiteSpace: "nowrap"
              }}>
                    <input type="radio" name="usageCategory" checked={category === c} onChange={() => {
                  setCategory(c);
                  setRows([]);
                  setUnmatchedRows([]);
                  setSearched(false);
                  setCellDetail(null);
                  setBranchDetail(null);
                }} />
                    {CATEGORY_LABEL[c]}
                  </label>)}
              </div>
            </Field>

            <Field label="Rows" hint="One line per product for the whole organisation instead of one per branch. Answers 'how much of this do we get through', which is the question when judging a supplier deal or what to hold centrally.">
              <label style={{
              display: "flex",
              gap: 6,
              alignItems: "center",
              fontSize: "0.9em",
              padding: "6px 0"
            }}>
                <input type="checkbox" checked={aggregate} onChange={e => {
                setAggregate(e.target.checked);
                setRows([]);
                setSearched(false);
                setCellDetail(null);
                setBranchDetail(null);
              }} />
                Aggregate across branches
              </label>
              <label style={{
              display: "flex",
              gap: 6,
              alignItems: "center",
              marginTop: 4,
              fontSize: "0.85em"
            }} title={"Lines a branch dispensed that never matched a dm+d product. " + "Shown so they are visible, but excluded from every average, " + "spend figure and excess calculation on these screens."}>
                <input type="checkbox" checked={showUnmatched} onChange={e => setShowUnmatched(e.target.checked)} />
                Show unmatched
              </label>
            </Field>

            <button onClick={run} disabled={busy} style={{
            padding: "8px 20px",
            fontWeight: 600,
            border: "none",
            borderRadius: 5,
            background: busy ? "#9db8dd" : "#2b7de9",
            color: "#fff"
          }}>
              {busy ? "Loading…" : "Show usage"}
            </button>

            <div style={{
            flex: 1
          }} />

            <button onClick={exportExcel} disabled={busy || exportBusy || !groupId} title="Export all products matching the selected filters, including rows beyond the display limit." style={{
            padding: "8px 16px",
            fontWeight: 600,
            border: "1px solid #1d7a4c",
            borderRadius: 5,
            background: busy || exportBusy || !groupId ? "#F0F0F0" : "#eaf7f0",
            color: busy || exportBusy || !groupId ? "#8C8C8C" : "#14653f",
            borderColor: busy || exportBusy || !groupId ? "#D9D9D9" : "#1d7a4c"
          }}>
              {exportBusy ? "Exporting…" : "Export to Excel"}
            </button>
          </div>

          {/* row 2 — value filters, kept visually separate from the search */}
          <div style={{
          display: "flex",
          gap: 14,
          flexWrap: "wrap",
          alignItems: "flex-end",
          marginTop: 10,
          paddingTop: 10,
          borderTop: "1px dashed #D9D9D9"
        }}>
            <div style={{
            ...fieldLabel,
            marginBottom: 0,
            alignSelf: "center",
            paddingBottom: 6
          }}>
              Hide low-value lines
            </div>

            <Field label="Min item cost (£)" hint="Pack price. Drops cheap lines whatever their volume.">
              <input type="number" min="0" step="0.01" value={minPrice} disabled={!havePrices} onChange={e => setMinPrice(e.target.value)} placeholder="any" style={{
              ...inputStyle,
              width: 110
            }} />
            </Field>

            <Field label="Min total avg mthly spend (£)" hint="Pack price × average packets a month. The one that finds where the money actually goes.">
              <input type="number" min="0" step="1" value={minSpend} disabled={!havePrices} onChange={e => setMinSpend(e.target.value)} placeholder="any" style={{
              ...inputStyle,
              width: 130
            }} />
            </Field>

            <Field label="Min avg mthly packet count" hint="Average packets a month. Drops slow movers regardless of price.">
              <input type="number" min="0" step="1" value={minPackets} onChange={e => setMinPackets(e.target.value)} placeholder="any" style={{
              ...inputStyle,
              width: 130
            }} />
            </Field>

            <Field label="Min usage confidence" hint={"The same verdict the estate overview puts on a line. Steady means " + "three months that agree closely; fair means three months that are " + "uneven but usable. A line with a month at zero, or a month missing " + "from the record, counts as no usage however big the other two are — " + "an average over a gap is not an average."}>
              <div style={{
              display: "flex",
              gap: 8,
              alignItems: "center"
            }}>
                <select value={minConf} onChange={e => setMinConf(e.target.value)} style={{
                ...inputStyle,
                width: 150
              }}>
                  {CONF_CHOICES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
                <label style={{
                display: "flex",
                gap: 5,
                alignItems: "center",
                fontSize: "0.85em"
              }}>
                  <input type="checkbox" checked={applyConf} onChange={e => setApplyConf(e.target.checked)} />
                  Apply
                </label>
              </div>
            </Field>

            <button onClick={clearFilters} disabled={!filtersOn} style={{
            padding: "6px 12px"
          }}>Clear</button>

            <div style={{
            flex: 1
          }} />

            {/* the count, and what it is a count of */}
            <div style={{
            fontSize: "0.85em",
            color: "#434343",
            textAlign: "right",
            lineHeight: 1.5
          }}>
              {searched && <>
                  <strong>{visible.length.toLocaleString()}</strong> product line{visible.length === 1 ? "" : "s"}
                  {filtersOn && rows.length !== visible.length && <span style={{
                color: "#5D5D5D"
              }}> of {rows.length.toLocaleString()}</span>}
                  {months.length > 0 && <> &middot; {months.length} month{months.length === 1 ? "" : "s"}
                      {" "}({months[0].label} &ndash; {months[months.length - 1].label})</>}
                  {showUnmatched && unmatchedRows.length > 0 && <div style={{
                color: "#8a6d1f",
                fontWeight: 600
              }}>
                      + {unmatchedRows.length.toLocaleString()} unmatched line
                      {unmatchedRows.length === 1 ? "" : "s"} shown first, not counted
                    </div>}
                  {havePrices && visible.length > 0 && <div>Avg spend shown: <strong>{money(totalSpend)}</strong> a month</div>}
                </>}
            </div>
          </div>
        </div>

        {/* ---- what the numbers mean ---- */}
        <div style={{
        background: "#f6f8fa",
        border: "1px solid #dfe3e8",
        padding: "8px 10px",
        marginBottom: 10,
        fontSize: "0.9em",
        lineHeight: 1.45
      }}>
          <strong>Each bar is one month on its own</strong> &mdash; not a running total.
          The window is the <strong>last three completed months</strong>; the current month is
          left out because it is always part way through. The average on the right is
          divided by the months we actually hold data for, and each line says which.
          The number under a bar is <strong>packets</strong>, converted from the units
          dispensed using the pack size shown; hover a bar for the unit figure.
          A product with no pack size cannot be converted, so it shows units only.
          {" "}
          {havePrices ? <>Pack price is the median price held against that product on the group&rsquo;s
                stock lines, so spend is an estimate of what the dispensing costs at those
                prices &mdash; not an invoice.</> : <span style={{
          color: "#a05a00"
        }}>
                No stock prices are loaded on this screen, so the price and spend columns are
                empty and the two money filters are switched off.
              </span>}
          {priced.some(r => r.apportioned) && <>
              <br />
              Lines marked <strong>est.</strong> came from an older PMR report that gave
              one total for a whole period, so its months are an even split rather than
              a measurement. Re&#8209;import that site to replace them.
            </>}
        </div>

        {/* The cap is the server's, not this screen's. Said plainly, because a
            table that silently stops at a round number reads as "that is all
            there is" and it is not. */}
        {searched && maybeCapped && <div style={{
        background: "#fff8e1",
        border: "1px solid #e6d28a",
        color: "#7a5c00",
        padding: "8px 10px",
        marginBottom: 10,
        fontSize: "0.88em",
        lineHeight: 1.45
      }}>
            The display is limited to {DISPLAY_LIMIT.toLocaleString()} product lines and may
            omit further matches. Narrow the search to see other products. Export to Excel
            includes every product matching the selected filters, beyond this display limit.
          </div>}

        {searched && rows.length > 0 && visible.length === 0 && <div style={{
        padding: "14px 4px",
        color: "#5D5D5D"
      }}>
            Every line was filtered out. Lower the thresholds above, or clear them.
          </div>}

        {applyConf && <div style={{
        fontSize: "0.85em",
        color: "#434343",
        marginBottom: 8
      }}>
            Showing only lines rated{" "}
            <strong style={{
          color: CONF[minConf].colour
        }}>{CONF[minConf].label}</strong>
            {minConf !== "low" && " or better"}, with no missing or zero month
            {unsteadyHidden > 0 && <> &mdash; {unsteadyHidden.toLocaleString()} line
              {unsteadyHidden === 1 ? "" : "s"} dropped as too unreliable to average</>}.
          </div>}

        {unpricedHidden > 0 && <div style={{
        fontSize: "0.85em",
        color: "#a05a00",
        marginBottom: 8
      }}>
            {unpricedHidden.toLocaleString()} line{unpricedHidden === 1 ? " has" : "s have"} no
            price on file and cannot meet a money filter, so {unpricedHidden === 1 ? "it is" : "they are"} hidden
            while one is set.
          </div>}

        {visible.length > 0 && <table style={{
        width: "100%",
        borderCollapse: "collapse",
        fontSize: "0.9em",
        border: "1px solid #D9D9D9"
      }}>
            <thead>
              <tr>
                <th style={th}>Pharmacy</th>
                <th style={th}>Product</th>
                <th style={{
              ...th,
              textAlign: "center"
            }}>Pack</th>
                <th style={{
              ...th,
              textAlign: "right"
            }} title="Median price held against this product on the group's stock lines.">
                  Pack price
                </th>
                <th style={th}>Usage by month (packets)</th>
              </tr>
            </thead>
            <tbody>
              {showUnmatched && unmatchedRows.length > 0 && <tr>
                  <td colSpan={5} style={{
              padding: "8px 9px",
              background: "#f7e9cd",
              borderBottom: "1px solid #e0cfa6",
              borderTop: "2px solid #e0cfa6"
            }}>
                    <strong>{unmatchedRows.length}</strong> {CATEGORY_LABEL[category].toLowerCase()}{" "}
                    line{unmatchedRows.length === 1 ? "" : "s"} dispensed but never matched to a
                    dm+d product. The usage below is real and the branch got through it, but
                    nothing on these screens counts it - no average, no excess, no spend.
                    Accept or allocate a line to bring it in.
                  </td>
                </tr>}
              {showUnmatched && unmatchedRows.map((r, i) => <tr key={`u${i}`} style={{
            background: "#fdf3e3"
          }}>
                  <td style={{
              ...td,
              whiteSpace: "nowrap"
            }}>
                    {r.aggregated ? <em>{r.pharmacyName}</em> : displayName(r.pharmacyName || r.pharmacyId, masked)}
                  </td>
                  <td style={td}>
                    {r.drugname}
                    <div style={{
                fontSize: "0.78em",
                color: "#8a6d1f",
                marginTop: 2
              }}>
                      Not matched to dm+d &middot; free-standing usage, counted nowhere
                      {r.branchCount > 1 && ` \u00b7 ${r.branchCount} branches`}
                    </div>
                    {r.suggestion && <div style={{
                fontSize: "0.78em",
                color: "#555",
                marginTop: 2
              }}>
                        Best guess: {r.suggestion.vmppNm}{" "}
                        <span style={{
                  color: "#888"
                }}>({r.suggestion.score})</span>
                      </div>}
                    {r.matchKey && <div style={{
                fontSize: "0.74em",
                color: "#8C8C8C",
                marginTop: 3
              }}>
                        {["medication", "supplement", "appliance"].filter(c => c !== category).map(c => <button key={c} disabled={moving === r.matchKey} onClick={() => moveCategory(r, c)} title={`Move this line to ${CATEGORY_LABEL[c]} and keep it there`} style={{
                  fontSize: "0.95em",
                  padding: "0 5px",
                  marginRight: 4,
                  border: "1px solid #e0cfa6",
                  background: "#fffdf8",
                  color: "#8a6d1f",
                  cursor: "pointer"
                }}>
                              &rarr; {CATEGORY_LABEL[c]}
                            </button>)}
                      </div>}
                    {r.matchKey && <div style={{
                marginTop: 4
              }}>
                        {r.suggestion && <button disabled={allocBusy === r.matchKey} onClick={() => allocate(r, r.suggestion.vppid, r.suggestion.vmppNm)} style={{
                  fontSize: "0.76em",
                  padding: "1px 6px",
                  marginRight: 5
                }}>
                            Accept
                          </button>}
                        <button disabled={allocBusy === r.matchKey} onClick={() => setAllocating(r)} style={{
                  fontSize: "0.76em",
                  padding: "1px 6px"
                }}>
                          Allocate
                        </button>
                      </div>}
                  </td>
                  <td style={{
              ...td,
              textAlign: "center",
              whiteSpace: "nowrap"
            }}>
                    {r.packSize ? `${r.packSize}${r.unit || ""}` : "\u2014"}
                  </td>
                  <td style={{
              ...td,
              textAlign: "right",
              color: "#8C8C8C"
            }}>&mdash;</td>
                  <td style={td}>
                    <div style={{
                display: "flex",
                gap: "10px",
                alignItems: "flex-end"
              }}>
                      {usageBars(r.months)}
                      <div style={{
                  borderLeft: "2px solid #ccc",
                  paddingLeft: "10px",
                  marginLeft: "4px",
                  minWidth: "190px",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center"
                }}>
                        <div style={{
                    fontSize: "0.82em",
                    color: "#333",
                    whiteSpace: "nowrap"
                  }}>
                          Avg use per month:{" "}
                          <strong>
                            {r.perMonthPackets != null ? `${r.perMonthPackets.toLocaleString()} pk` : `${(r.perMonthUnits ?? 0).toLocaleString()} units`}
                          </strong>
                        </div>
                        <div style={{
                    fontSize: "0.76em",
                    color: "#777"
                  }}>{r.basis}</div>
                      </div>
                    </div>
                  </td>
                </tr>)}
              {showUnmatched && unmatchedRows.length > 0 && visible.length > 0 && <tr>
                  <td colSpan={5} style={{
              padding: "6px 9px",
              background: "#F5F5F5",
              borderTop: "2px solid #D9D9D9",
              borderBottom: "1px solid #F0F0F0",
              color: "#5D5D5D"
            }}>
                    Matched and counted below
                  </td>
                </tr>}
              {visible.map((r, i) => {
            const rowMonths = Array.isArray(r.months) ? r.months : [];
            const peak = Math.max(...rowMonths.map(m => m.units || 0), 0) || 1;
            return <React.Fragment key={i}>
                  <tr>
                    <td style={{
                  ...td,
                  whiteSpace: "nowrap"
                }}>
                      {r.aggregated ? <button type="button" onClick={() => openBranchBreakdown(r)} aria-expanded={!!(branchDetail && branchDetail.row.snomed === r.snomed && branchDetail.row.drugname === r.drugname)} title="Show usage by pharmacy" style={{
                    border: 0,
                    background: "none",
                    padding: 0,
                    color: "inherit",
                    font: "inherit",
                    cursor: "pointer",
                    textAlign: "left"
                  }}>
                          {branchDetail && branchDetail.row.snomed === r.snomed && branchDetail.row.drugname === r.drugname ? "▾ " : "▸ "}
                          <em>{r.pharmacyName}</em>
                        </button> : displayName(r.pharmacyName || r.pharmacyId, masked)}
                    </td>
                    <td style={td}>
                      <span style={{
                    cursor: r.snomed ? "pointer" : "default"
                  }} title={r.snomed ? "Show which PMR lines are counted under this dm+d product" : ""} onClick={() => r.snomed && setOpenMatches(p => ({
                    ...p,
                    [`${r.pharmacyId || "agg"}_${r.snomed}`]: !p[`${r.pharmacyId || "agg"}_${r.snomed}`]
                  }))}>
                        {r.snomed ? openMatches[`${r.pharmacyId || "agg"}_${r.snomed}`] ? "\u25be " : "\u25b8 " : ""}
                        {r.drugname}
                      </span>
                      {/* The search looks at the branch's own wording as well
                          as the dm+d name, so a hit can have none of the typed
                          words visible on it. Say which spelling matched. */}
                      {(r.snomed || r.matchKey) && <div style={{
                    fontSize: "0.74em",
                    color: "#8C8C8C",
                    marginTop: 3
                  }}>
                          {["medication", "supplement", "appliance"].filter(c => c !== category).map(c => <button key={c} disabled={moving === (r.snomed || r.matchKey)} onClick={() => moveCategory(r, c)} title={`Move this line to ${CATEGORY_LABEL[c]} and keep it there`} style={{
                      fontSize: "0.95em",
                      padding: "0 5px",
                      marginRight: 4,
                      border: "1px solid #F0F0F0",
                      background: "#fff",
                      color: "#5D5D5D",
                      cursor: "pointer"
                    }}>
                                &rarr; {CATEGORY_LABEL[c]}
                              </button>)}
                          {r.categoryMoved && <button disabled={moving === (r.snomed || r.matchKey)} onClick={() => moveCategory(r, "auto")} title="Put this line back where the automatic split would file it" style={{
                      fontSize: "0.95em",
                      padding: "0 5px",
                      border: "1px solid #F0F0F0",
                      background: "#fff",
                      color: "#5D5D5D",
                      cursor: "pointer"
                    }}>
                              reset
                            </button>}
                        </div>}
                      {Array.isArray(r.matchedOn) && r.matchedOn.length > 0 && <div style={{
                    fontSize: "0.76em",
                    color: "#5D5D5D",
                    marginTop: 2
                  }}>
                          matched on: {r.matchedOn.map(k => k.split("|")[0]).join(", ")}
                        </div>}
                      {r.apportioned && <span style={{
                    marginLeft: 6,
                    fontSize: "0.8em",
                    color: "#a00"
                  }}>est.</span>}

                      {openMatches[`${r.pharmacyId || "agg"}_${r.snomed}`] && <PmrMatchLines groupId={groupId} snomed={r.snomed} {...r.aggregated ? {} : {
                    pharmacyId: r.pharmacyId
                  }} onChanged={run} />}
                    </td>
                    <td style={{
                  ...td,
                  textAlign: "center",
                  whiteSpace: "nowrap"
                }}>
                      {r.packSize ? `${r.packSize}${r.unit || ""}` : "\u2014"}
                    </td>
                    <td style={{
                  ...td,
                  textAlign: "right",
                  whiteSpace: "nowrap",
                  color: r._price == null ? "#8C8C8C" : "#262626"
                }}>
                      {money(r._price)}
                    </td>
                    <td style={td}>
                      <div style={{
                    display: "flex",
                    gap: "6px",
                    alignItems: "flex-end"
                  }}>
                        {rowMonths.map(m => <div key={m.month} onClick={() => openCell(r, m)} style={{
                      textAlign: "center",
                      minWidth: "44px",
                      cursor: "pointer"
                    }}>
                            <div title={`${m.label}: ${(m.units || 0).toLocaleString()} units \u2014 click for the breakdown`} style={{
                        height: "40px",
                        display: "flex",
                        alignItems: "flex-end",
                        justifyContent: "center"
                      }}>
                              <div style={{
                          width: "22px",
                          // a real but tiny month must still be visible,
                          // and a real zero must look different from it
                          height: `${m.units > 0 ? Math.max(3, m.units / peak * 40) : 0}px`,
                          background: m.units > 0 ? "#2b7de9" : "transparent",
                          borderBottom: m.units > 0 ? "none" : "2px solid #ccc"
                        }} />
                            </div>
                            <div style={{
                        fontSize: "0.78em",
                        marginTop: 2
                      }}>
                              {m.packets != null ? m.packets.toLocaleString() : (m.units || 0).toLocaleString()}
                            </div>
                            <div style={{
                        fontSize: "0.7em",
                        color: "#777"
                      }}>
                              {String(m.label || "").replace(" 20", " ")}
                            </div>
                          </div>)}

                        {/* The average, not the total. A three-month total says
                            what happened; a monthly average is what you order and
                            transfer against, and it is the figure every other
                            screen here uses - and now what it costs. */}
                        <div style={{
                      borderLeft: "2px solid #ccc",
                      paddingLeft: "10px",
                      marginLeft: "4px",
                      alignSelf: "stretch",
                      minWidth: "190px",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center"
                    }} title={"The monthly average is based on whatever data is available over " + "the last three completed months from today's date. If no data is " + "available, then no usage is shown." + `\n\nConfidence: ${CONF[r._conf.level].label} — ${r._conf.why}.`}>
                          {r.monthsLoaded > 0 ? <>
                              <div style={{
                          fontSize: "0.82em",
                          color: "#333",
                          whiteSpace: "nowrap"
                        }}>
                                Avg use per month:{" "}
                                <strong>
                                  {r.perMonthPackets != null ? `${r.perMonthPackets.toLocaleString()} pk` : `${(r.perMonthUnits ?? 0).toLocaleString()} units`}
                                </strong>
                              </div>
                              <div style={{
                          fontSize: "0.82em",
                          whiteSpace: "nowrap",
                          color: r._spend == null ? "#8C8C8C" : "#14653f"
                        }}>
                                Avg spend per month: <strong>{money(r._spend)}</strong>
                              </div>
                              <div style={{
                          fontSize: "0.76em",
                          color: "#777"
                        }}>
                                {r.basis}
                                {" \u00b7 "}
                                <span title={r._conf.why} style={{
                            color: CONF[r._conf.level].colour,
                            fontWeight: 700,
                            cursor: "help"
                          }}>
                                  {CONF[r._conf.level].label}
                                </span>
                              </div>
                              {r.aggregated && <button onClick={() => openBranchBreakdown(r)} title="Which branches this average is made of, what each of them gets through and what it costs them" style={{
                          marginTop: 3,
                          fontSize: "0.76em",
                          padding: "1px 6px"
                        }}>
                                  {r.branchCount} branch{r.branchCount === 1 ? "" : "es"} with data
                                </button>}
                            </> : <div style={{
                        fontSize: "0.82em",
                        color: "#999"
                      }}>No usage data</div>}
                        </div>
                      </div>

                      {/* The month breakdown opens under the row it belongs to,
                          not in a second dialogue on top of the first. */}
                      {cellDetail && cellDetail.row.snomed === r.snomed && cellDetail.row.pharmacyId === r.pharmacyId && <div style={{
                    marginTop: "8px",
                    border: "1px solid #ccc",
                    background: "#fbfcfd",
                    padding: "8px 10px"
                  }}>
                          <div style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center"
                    }}>
                            <strong>{r.drugname} &mdash; {cellDetail.label}</strong>
                            <button onClick={() => setCellDetail(null)}>Close</button>
                          </div>

                          {cellBusy && <div style={{
                      marginTop: 6
                    }}>Loading&hellip;</div>}

                          {!cellBusy && cellDetail.rows && cellDetail.rows.length === 0 && <div style={{
                      marginTop: 6,
                      color: "#777"
                    }}>Nothing stored for that month.</div>}

                          {!cellBusy && cellDetail.rows && cellDetail.rows.length > 0 && <table border="1" cellPadding="4" style={{
                      width: "100%",
                      marginTop: "6px",
                      fontSize: "0.88em",
                      background: "#fff"
                    }}>
                              <thead>
                                <tr>
                                  <th style={{
                            textAlign: "left"
                          }}>Pharmacy</th>
                                  <th style={{
                            textAlign: "left"
                          }}>Stored as</th>
                                  <th>Units</th>
                                  <th>Packets</th>
                                  <th>Behind it</th>
                                  <th>Source</th>
                                </tr>
                              </thead>
                              <tbody>
                                {cellDetail.rows.map((d, j) => <tr key={j}>
                                    <td>{displayName(d.pharmacyName, masked)}</td>
                                    <td>{d.drugname}</td>
                                    <td style={{
                            textAlign: "right"
                          }}>{(d.units || 0).toLocaleString()}</td>
                                    <td style={{
                            textAlign: "right"
                          }}>
                                      {d.packets != null ? d.packets.toLocaleString() : "\u2014"}
                                    </td>
                                    <td style={{
                            textAlign: "right"
                          }}>
                                      {d.events != null ? `${d.events.toLocaleString()} ${d.eventLabel}` : "\u2014"}
                                    </td>
                                    <td>
                                      {d.source}
                                      {d.apportioned && <span style={{
                              color: "#a00"
                            }}> est.</span>}
                                      {d.partialMonth && <span style={{
                              color: "#a60"
                            }}> part mth</span>}
                                    </td>
                                  </tr>)}
                              </tbody>
                            </table>}

                          <div style={{
                      marginTop: "6px",
                      fontSize: "0.82em",
                      color: "#666"
                    }}>
                            This is as far down as the data goes. Both feeds are stored
                            per pharmacy, product and month &mdash; the NHS extract's
                            per-patient lines are combined on import and the PMR export's
                            individual dispensings are summed before they are saved.
                          </div>
                        </div>}
                    </td>
                  </tr>
                  {r.aggregated && branchDetail && branchDetail.row.snomed === r.snomed && branchDetail.row.drugname === r.drugname && <tr>
                      <td colSpan={5} style={{
                  ...td,
                  background: "#fbfcfd"
                }}>
                      {/* Branch breakdown, under the row it explains. */}
                      {branchDetail && branchDetail.row.snomed === r.snomed && branchDetail.row.drugname === r.drugname && <div style={{
                    marginTop: 8,
                    border: "1px solid #ccc",
                    background: "#fbfcfd",
                    padding: "8px 10px"
                  }}>
                          <div style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center"
                    }}>
                            <strong>{r.drugname} &mdash; by branch</strong>
                            <button onClick={() => setBranchDetail(null)}>Close</button>
                          </div>

                          {branchBusy && <div style={{
                      marginTop: 6
                    }}>Loading&hellip;</div>}

                          {!branchBusy && branchDetail.branches && branchDetail.branches.length === 0 && <div style={{
                      marginTop: 6,
                      color: "#777"
                    }}>
                              No branch dispensed this in the window.
                            </div>}

                          {!branchBusy && branchDetail.branches && branchDetail.branches.length > 0 && <table border="1" cellPadding="6" style={{
                      width: "100%",
                      marginTop: 6,
                      fontSize: "0.88em",
                      background: "#fff"
                    }}>
                              <thead>
                                <tr>
                                  <th style={{
                            textAlign: "left"
                          }}>Branch</th>
                                  <th style={{
                            textAlign: "left"
                          }}>Usage by month</th>
                                  <th style={{
                            textAlign: "left"
                          }}>Avg use per month</th>
                                  <th style={{
                            textAlign: "left"
                          }}>Avg spend per month</th>
                                </tr>
                              </thead>
                              <tbody>
                                {branchDetail.branches.map(b => {
                          // The branch's own packets at the product's price -
                          // the same arithmetic as the estate line above, so
                          // the parts add up to the whole in front of a client.
                          const bSpend = r._price != null && b.perMonthPackets != null ? r._price * b.perMonthPackets : null;
                          return <tr key={b.pharmacyId}>
                                      <td style={{
                              whiteSpace: "nowrap"
                            }}>
                                        {displayName(b.pharmacyName, masked)}
                                      </td>
                                      <td>{usageBars(b.months)}</td>
                                      <td style={{
                              whiteSpace: "nowrap"
                            }}>
                                        <strong>
                                          {b.perMonthPackets != null ? `${b.perMonthPackets.toLocaleString()} pk` : `${(b.perMonthUnits ?? 0).toLocaleString()} units`}
                                        </strong>
                                        <div style={{
                                fontSize: "0.85em",
                                color: "#777"
                              }}>{b.basis}</div>
                                      </td>
                                      <td style={{
                              whiteSpace: "nowrap",
                              color: bSpend == null ? "#8C8C8C" : "#14653f"
                            }}>
                                        <strong>{money(bSpend)}</strong>
                                      </td>
                                    </tr>;
                        })}
                              </tbody>
                            </table>}
                        </div>}

                      </td>
                    </tr>}
                  </React.Fragment>;
          })}
            </tbody>
          </table>}

          {allocating && <RemapDialog line={{
        title: "Allocate this line to the right dm+d product",
        label: allocating.drugname,
        packSize: allocating.packSize,
        units: allocating.total
      }} onCancel={() => setAllocating(null)} onPick={c => allocate(allocating, c.vppid, c.vmppNm)} />}
      </div>
    </>;
}

/* One row of month bars, for the branch breakdown. Each set is scaled against
   its OWN peak: a shared scale across rows flattens every slow mover into an
   invisible line next to the Fortisips, and the question a row answers is
   "what is this product's own pattern", not "how big is it next to others". */
export function usageBars(months = [], opts = {}) {
  const peak = Math.max(...months.map(m => m.units || 0), 0) || 1;
  const h = opts.height || 30;
  return <div style={{
    display: "flex",
    gap: "5px",
    alignItems: "flex-end"
  }}>
      {months.map(m => <div key={m.month} style={{
      textAlign: "center",
      minWidth: "40px"
    }}>
          <div title={`${m.label}: ${(m.units || 0).toLocaleString()} units`} style={{
        height: `${h}px`,
        display: "flex",
        alignItems: "flex-end",
        justifyContent: "center"
      }}>
            <div style={{
          width: "18px",
          height: `${m.units > 0 ? Math.max(3, m.units / peak * h) : 0}px`,
          background: m.units > 0 ? "#2b7de9" : "transparent",
          borderBottom: m.units > 0 ? "none" : "2px solid #ccc"
        }} />
          </div>
          <div style={{
        fontSize: "0.75em",
        lineHeight: 1.2
      }}>
            {m.packets != null ? m.packets.toLocaleString() : (m.units || 0).toLocaleString()}
          </div>
          <div style={{
        fontSize: "0.68em",
        color: "#888",
        whiteSpace: "nowrap"
      }}>
            {String(m.label || "").replace(" 20", " ")}
          </div>
        </div>)}
    </div>;
}
// Paired reports are now the default usage view for both stock-analysis entry points.
export { default } from "./usage_pair_grid";
